import type { QueryReturnValue } from '@reduxjs/toolkit/query/react';

import type { AppError } from '../../utils/firebaseErrors';
import { isBackendGatewayError, toAppError } from '../../utils/backendGatewayError';
import { buildReportsTableStaleReadKeys } from '../staleReadFallback/queryKeys';
import {
  recordSuccessfulRead,
  resolveStaleReadFallback,
  type StaleReadMeta,
} from '../staleReadFallback/staleReadCache';
import {
  assertReportListQueryCapabilities,
  toCanonicalReportFilters,
} from './reportQueryCapabilities';
import { toReportInvalidQueryError } from './reportQueryErrors';
import {
  getLastValidReportListPage,
  pruneReportPresenceLostListPage,
} from './reportReadFamilyPolicy';
import type {
  BackofficeCanonicalReportFilterItem,
  BackofficeReportProjection,
  BackofficeReportSort,
} from './reportsGateway';

type ReportsFilterItem = {
  field: string;
  op: string;
  value: string | string[];
};

type PaginatedReportsFrontend = {
  items: BackofficeReportProjection[];
  page: number;
  pageSize: number;
  totalCount: number;
  staleRead?: StaleReadMeta;
};

type ReportSort = {
  field: string;
  direction: string;
};

export const DEFAULT_REPORT_SORT: BackofficeReportSort = {
  field: 'sightingDate',
  direction: 'desc',
};

const hasText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const cleanFilters = (filters: ReportsFilterItem[]) =>
  filters.filter(
    (item) =>
      hasText(item.field) &&
      hasText(item.op) &&
      item.value !== undefined &&
      item.value !== null &&
      item.value !== ''
  );

const isInvalidQueryFailure = (error: unknown): boolean => {
  if (isBackendGatewayError(error)) {
    if (error.reason.includes('invalid-query')) {
      return true;
    }

    return isInvalidQueryFailure(error.original);
  }

  if (!error || typeof error !== 'object') {
    return false;
  }

  const record = error as { code?: unknown; reason?: unknown };
  const code = typeof record.code === 'string' ? record.code : '';
  const reason = typeof record.reason === 'string' ? record.reason : '';

  return (
    code.includes('invalid-argument') ||
    code.includes('invalid-query') ||
    reason.includes('invalid-query')
  );
};

const addStaleReadMeta = <T>(data: T, staleRead: StaleReadMeta): T => {
  if (Array.isArray(data)) {
    return Object.assign([...data], { staleRead }) as T;
  }

  if (data && typeof data === 'object') {
    return Object.assign({}, data, { staleRead }) as T;
  }

  return data;
};

export const readReportListFamily = async ({
  filters,
  page,
  pageSize,
  sort,
  read,
  onPageCorrection,
}: {
  filters: ReportsFilterItem[];
  page: number;
  pageSize: number;
  sort?: ReportSort;
  read: (params: {
    filters: BackofficeCanonicalReportFilterItem[];
    page: number;
    pageSize: number;
    sort: BackofficeReportSort;
  }) => Promise<PaginatedReportsFrontend>;
  onPageCorrection?: (page: number) => void;
}): Promise<QueryReturnValue<PaginatedReportsFrontend, AppError, {}>> => {
  const normalizedFilters = cleanFilters(filters);
  const effectiveSort = (sort ?? DEFAULT_REPORT_SORT) as BackofficeReportSort;
  let canonicalFilters: BackofficeCanonicalReportFilterItem[];
  try {
    assertReportListQueryCapabilities(normalizedFilters, effectiveSort);
    canonicalFilters = toCanonicalReportFilters(normalizedFilters);
  } catch (error: unknown) {
    return { error: toReportInvalidQueryError(error) };
  }

  const staleReadKeys = buildReportsTableStaleReadKeys({
    filters: normalizedFilters,
    page,
    pageSize,
    sort: effectiveSort,
  });

  try {
    const result = await read({
      filters: canonicalFilters,
      page,
      pageSize,
      sort: effectiveSort,
    });
    const visibleResult = pruneReportPresenceLostListPage(result);

    const lastValidPage = getLastValidReportListPage(visibleResult.totalCount, pageSize);

    if (page > lastValidPage && visibleResult.items.length === 0) {
      onPageCorrection?.(lastValidPage);

      return {
        data: {
          items: visibleResult.items,
          page: visibleResult.page,
          pageSize: visibleResult.pageSize,
          totalCount: visibleResult.totalCount,
        },
      };
    }

    recordSuccessfulRead(staleReadKeys.exactKey, staleReadKeys.familyKey, visibleResult);

    return { data: visibleResult };
  } catch (error: unknown) {
    if (isInvalidQueryFailure(error)) {
      return { error: toReportInvalidQueryError(error) };
    }

    const staleResult = resolveStaleReadFallback<PaginatedReportsFrontend>(
      staleReadKeys.exactKey,
      staleReadKeys.familyKey,
      true
    );

    if (staleResult) {
      return {
        data: addStaleReadMeta(
          pruneReportPresenceLostListPage(staleResult.data),
          staleResult.staleRead
        ),
      };
    }

    return { error: toAppError(error) };
  }
};
